import {
  Link, NavLink, Outlet, useLocation, useMatch, useNavigate, useOutletContext,
} from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ErrorBoundary } from "./ErrorBoundary";
import { getBuild, getOverview, type CourseAccess, type Me, type Overview } from "./api";
import { LiveProvider, Spin, useLiveFeed } from "./live";
import { LivePill, Toasts } from "./LiveBar";
import { NAV_GROUPS, PAGE_LABELS, routes, type Page } from "./routes";
import { useAsync, type Async } from "./useAsync";
import { ThemeToggle } from "./ui";

/** What every page under the shell can reach: the cohort it is for, and the one shared query.
 *
 * 🔴 ONE `getOverview` FOR THE WHOLE SHELL. The sidebar's counts and the Overview's cards are
 * the same document; fetching it twice would mean the badge and the card could disagree, and
 * would double the load of the only route that refreshes on a timer. */
export interface Shell {
  courseId: string;
  access: CourseAccess;
  overview: Async<Overview>;
  /** 🔴 ESTATE-WIDE, NOT PER COHORT — which is exactly why it is not in `access`.
   *  `CourseAccess` is six capabilities a cohort grants; being an administrator is none of
   *  them, because "may administer, but only over there" is not a rule this console could
   *  enforce. The Rubrics card reads it for the same reason the sidebar does: editing a
   *  rubric family changes every cohort at once, so the control cannot sit behind a
   *  per-cohort capability. Hiding grants nothing — the server refuses by name either way. */
  isOwner: boolean;
  /** Refetch `getMe`, which is where `access` and the cohort picker come from.
   *
   * 🔴 THE CONTROL AND THE BADGE CONTRADICTING IT SAT IN THE SAME `<Field>`. `me` is fetched
   * once at boot, so turning writing on reported success beside a badge still reading
   * `read-only`, and adding a cohort left the picker without it until a reload. That is the
   * same defect already recorded for the sidebar counts — invalidation, not caching — and
   * the fix is the same shape: a mutation that changes `me` says so. */
  reloadMe: () => void;
}

export function useShell(): Shell {
  return useOutletContext<Shell>();
}

/** How often the shell refetches. Long enough that a background tab is quiet, short enough
 *  that a teacher watching a run sees it land without pressing anything. */
const REFRESH_MS = 60_000;

/** A commit CI stamped into the image, or "" — `"unknown"` is a local or hand-built one. */
const known = (commit: string | undefined): string =>
  commit && /^[0-9a-f]{40}$/.test(commit) ? commit : "";

/**
 * Whether the server has been redeployed since this page loaded.
 *
 * 🔴 A TAB OPENED BEFORE A DEPLOY GOES ON RUNNING THE CODE FROM BEFORE IT. [X] 2026-09-22, on
 * dev: the owner's Teachers page showed five columns after the sixth had shipped, and a save
 * from it would have sent five fields. Nothing in an open tab ever asked which build the server
 * was running, although `/api/health` has said so since 2026-09-09.
 * ⚠️ THE FIRST BUILD SEEN IS THE ONE THIS PAGE LOADED WITH. `index.html` is `no-cache`, so a
 * load always fetches the current page; what goes stale is a tab left open across a deploy.
 * A failed check and an unknown build say nothing either way.
 */
function useNewerBuild(): boolean {
  const build = useAsync(() => getBuild(), [], { every: REFRESH_MS });
  const now = known(build.data?.commit);
  const [loaded, setLoaded] = useState("");
  useEffect(() => { if (now && !loaded) setLoaded(now); }, [now, loaded]);
  return Boolean(loaded && now && now !== loaded);
}

export function Layout({ me, onSignOut, courseId, reloadMe }:
  { me: Me; onSignOut: () => void; courseId: string; reloadMe: () => void }) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const access = me.courses.find((c) => c.id === courseId) ?? me.courses[0]!;
  const overview = useAsync(() => getOverview(courseId), [courseId], { every: REFRESH_MS });
  const newer = useNewerBuild();
  // 🔴 WHAT IS RUNNING, FOR THE WHOLE SHELL (2026-10-06): one poll of one database-only feed, read
  // by the pill, the sidebar, the toasts and every page that shows a run.
  const live = useLiveFeed(courseId);
  // ⚠️ SOMETHING LANDED, SO THE SIDEBAR'S COUNTS MOVED: the review badge is the overview's, and
  // waiting a minute for its own timer is the stale badge this shell already paid for once.
  const lastEvent = useRef(0);
  const reloadOverview = overview.reload;
  useEffect(() => {
    const n = live.events.length ? live.events[live.events.length - 1]!.n : 0;
    if (n > lastEvent.current) { lastEvent.current = n; reloadOverview(); }
  }, [live.events, reloadOverview]);
  const grading = (live.feed?.counts.grading ?? 0) > 0;
  const grouping = (live.feed?.other ?? []).some((o) => o.kind === "team_grouping");

  // Which nav item is lit. `submissions/:sid` and `assignments/:cw` both belong to Assignments
  // — a report is one grade of one assignment, and Grades, which used to own it, is a filter of
  // that page since 2026-10-06 — so a report page does not leave the sidebar with nothing lit.
  // ⚠️ `grades` and `entry` are only ever seen for the instant before their redirect lands.
  const deep = useMatch("/c/:courseId/:page/*");
  const flat = useMatch("/c/:courseId/:page");
  const raw = (deep ?? flat)?.params.page ?? "overview";
  const current: Page = ["submissions", "grades", "entry"].includes(raw) ? "assignments"
    : (raw as Page);

  const d = overview.data;
  // 🔴 THE IN-REVIEW COUNT RIDES ON ASSIGNMENTS, where the grades it counts now live. The
  // ready count is not here: it is the page's own Ready cell and on the Overview, because a
  // sidebar that put two numbers on one item would not say which one a click was for.
  const counts: Partial<Record<Page, { n: number; tone: string; title: string }>> = {
    assignments: d && d.grades.review > 0
      ? { n: d.grades.review, tone: "warn",
          title: `${d.grades.review} grade(s) need a human before they go out` }
      : undefined,
    runs: d && d.runs.stored_nothing_7d > 0
      ? { n: d.runs.stored_nothing_7d, tone: "stop",
          title: `${d.runs.stored_nothing_7d} paid run(s) stored nothing in the last 7 days` }
      : undefined,
  };

  // ⚠️ TEACHERS IS HIDDEN WITHOUT `is_owner`, DEMO WHERE THE DEPLOYMENT ALLOWS NONE, AND NOTHING
  // ELSE. A page is hidden only when it would be entirely empty, never merely read-only:
  // Teachers answers 403 in full to anybody not on the list in code. Grading renders for a
  // viewer who cannot edit and says so, because not being able to see what the system is set to
  // is a worse failure than a disabled control.
  // ⚠️ ENTRY USED TO BE THE THIRD, hidden without `can_release`. It is a filter of Assignments
  // since 2026-10-06, shown to everybody with Mark entered off for whoever lacks Release — the
  // sidebar has nothing left to hide.
  // ⚠️ HIDING GRANTS NOTHING. `/teachers` is still routed and the server still refuses by
  // name, so somebody who has the address gets the sentence rather than a silent redirect.
  // ⚠️ AND `demo` ONLY WHERE THE DEPLOYMENT ALLOWS ONE. On production the page would offer a
  // button whose every press is refused; the page itself stays routed and says why.
  const visible = (p: Page) =>
    (p !== "teachers" || me.is_owner) && (p !== "demo" || me.demo_enabled);

  /** One group, drawn the same way on either side of the divider. */
  const navGroup = (g: (typeof NAV_GROUPS)[number]) => {
    const pages = g.pages.filter(visible);
    if (!pages.length) return null;
    return (
      <div className="nav-group" key={g.title}>
        {/* 🔴 WRITABILITY SITS ON THE COHORT HEADING, NOT AT THE FOOT OF THE ZONE (owner,
            2026-09-09). It is a fact about the cohort, so it belongs where the cohort is
            named; below seven nav items it read as a footnote about the sidebar.
            ⚠️ AND IT IS ONE WORD NOW. "writes to Classroom" under a heading that already says
            COHORT spent three words restating context the reader has; the sentence it
            abbreviates is on both badges as a `title`, because an abbreviation with no
            expansion anywhere is just a shorter guess. */}
        <div className="nav-group-title">
          <span>{g.title}</span>
          {g.scope === "cohort" && (
            access.write_allowed
              ? <span className="badge ok" title="A released mark or an announcement can reach
the students in this cohort. Every write is still one person's decision about one student.">
                  writes</span>
              : <span className="badge stop" title="Nothing in this console may write a grade,
a release or an announcement to this cohort. Reading is unaffected.">read-only</span>)}
        </div>
        {pages.map((p) => {
          const badge = counts[p];
          return (
            <NavLink key={p} to={
                       p === "grading" ? routes.grading(courseId)
                       : p === "applicants" ? routes.applicants(courseId)
                       : p === "teams" ? routes.teams(courseId)
                       : routes.page(courseId, p)}
                     className={({ isActive }) =>
                       isActive || current === p ? "nav-item on" : "nav-item"}>
              {PAGE_LABELS[p]}
              {/* ⚠️ A SPINNER BESIDE A PAGE WITH WORK IN FLIGHT, decorative: its name says what. */}
              {((grading && (p === "assignments" || p === "runs"))
                || (grouping && p === "teams")) && <Spin />}
              {badge && <>
                <span className="spacer" />
                <span className={`nav-count ${badge.tone}`} title={badge.title}>
                  {badge.n}</span>
              </>}
            </NavLink>);
        })}
      </div>);
  };

  return (
    <LiveProvider value={live}>
      <div>
        <header className="topbar">
          {/* ⚠️ NO "grading" TAG. It was accurate when the console did one thing; it now carries
              the roster, the social-activity sweep, the activity log and the health of the whole
              pipeline, and a label that names one of those makes the rest look like visitors. */}
          {/* 🔴 THE WORDMARK, NOT A COLOURED CIRCLE. The console wore an 8px dot in the old
              generic blue beside the word "Turbine" — a placeholder identity that survived the
              2026-09-06 brand work because that pass stopped at the applicant surface. The
              supplied asset is the only brand asset there is; it belongs where the product
              names itself.
              ⚠️ IT STAYS AN `h1`. The dot carried no text, so the topbar's only heading was the
              word beside it; the `alt` gives the same heading its accessible name, and every
              page's own `h2` still sits one level below it. */}
          <h1 className="brand">
            <img className="logo" src="/brand/turbin3-logo.svg" alt="Turbin3"
                 width={177} height={35} />
          </h1>
          <span className="spacer" />
          {/* 🔴 "IS THE MACHINE UP" SHOULD NOT NEED NAVIGATING TO. Rendered only when a health
              card is not OK, so an empty topbar is itself the reading.
              🔴 IT COSTS NO CALL. `getHealth` is the deliberately expensive route — it blocks
              on `windmill.workers()` and makes one sequential HTTP request per rubric — and
              this shell is mounted on every page, so calling it here would pay that on every
              navigation. Health cards already feed `overview.attention` server-side
              (`overview._attention`), severity-sorted, which is the same builder the Overview
              reads. One call, one authority, no second opinion about whether the machine is up.
              ⚠️ `page === "health"` IS THE FILTER, not "any attention item". The usage card
              (kind `usage`, once `spend`) routes to Grading because its remedy is a setting, and
              a pill that links to Health must only carry things Health explains. */}
          {(() => {
            const worst = d?.attention.find((a) => a.page === "health");
            if (!worst) return null;
            return (
              <Link className={`hpill ${worst.severity === "STOP" ? "stop" : "warn"}`}
                    to={routes.page(courseId, "health")} title={worst.detail}>
                <span className="hpill-dot" />
                <span className="hpill-word">{worst.severity}</span>
                <span className="hpill-name">{worst.title}</span>
              </Link>);
          })()}
          <LivePill courseId={courseId} me={me.email} />
          <span className="who">
            {me.display_name} · <button className="btn ghost sm" onClick={onSignOut}>
              Sign out</button>
          </span>
          <ThemeToggle />
        </header>

        <div className="layout">
          <aside className="sidebar">
            {/* 🔴 THE PICKER GOES INSIDE THE BRACKET IT GOVERNS. At the top of the sidebar it
                read as governing all twelve pages; it governs seven. Position does the
                explaining, so nothing needs a tooltip. */}
            <div className="zone">
              <div className="zone-head">
                <div className="kick">Viewing cohort</div>
                {me.courses.length > 1 ? (
                  <select className="picker" value={courseId} aria-label="Cohort"
                          onChange={(e) => navigate(routes.page(e.target.value, current))}>
                    {me.courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                ) : (
                  <span className="picker one">{access.name}</span>
                )}
              </div>
              <nav className="nav" aria-label="Cohort pages">
                {NAV_GROUPS.filter((g) => g.scope === "cohort").map(navGroup)}
              </nav>
            </div>

            {/* ⚠️ DECORATIVE. The scope it announces is carried by the next `nav`'s own
                accessible name, so a screen reader hears it once rather than as a stray word
                between two lists. */}
            <div className="divide" aria-hidden="true">
              <span className="divide-rule" />
              <span className="divide-label">All cohorts</span>
              <span className="divide-rule" />
            </div>

            <nav className="nav" aria-label="All cohorts">
              {NAV_GROUPS.filter((g) => g.scope === "all").map(navGroup)}
            </nav>

            <p className="sidebar-note">
              Operations and Settings cover every cohort. Open one and you can narrow it to a
              cohort.
            </p>
          </aside>

          {/* 🔴 `key` IS WHAT REMOUNTS A PAGE WHEN THE COHORT CHANGES. React Router does not
              remount a route element when only a param changes, so without this a slow request
              could paint one cohort's rows under another's heading — which is the exact failure
              `useAsync`'s sequence guard was added for, one level up. */}
          <main className="main" key={courseId}>
            {/* ⚠️ A NOTICE, NEVER A RELOAD. The page may hold an edit nobody has saved, so the
                teacher decides when; the sentence says what reloading costs. */}
            {newer && (
              <div className="alert warn" role="status">
                <span className="ico">↻</span>
                <div className="body">
                  <div className="t">A newer version of the console is live</div>
                  <div className="m">
                    This page is from before it, so what it shows may be out of date. Reload to
                    use the new one — anything you have not saved on this page will be lost.
                  </div>
                </div>
                <button className="btn sm" onClick={() => window.location.reload()}>
                  Reload</button>
              </div>)}
            {/* 🔴 ONE PAGE FAILING MUST NOT TAKE THE CONSOLE WITH IT. React unmounts the whole
                tree on an uncaught render error, so before this a single bad field reference
                left a teacher with a white screen and the reason only in devtools. Keyed on
                the path so walking away is the way out. */}
            <ErrorBoundary resetKey={pathname}>
              <Outlet context={{ courseId, access, overview, reloadMe,
                                 isOwner: me.is_owner } satisfies Shell} />
            </ErrorBoundary>
          </main>
        </div>
      </div>
      <Toasts courseId={courseId} />
    </LiveProvider>
  );
}
